import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import moment from "moment";
import { getUsers, getDailyByDate } from "../../services/newdaily";
import UserCard from "../../components/UserCard/UserCard";
import UserCardHeader from "../../components/UserCardHeader/UserCardHeader";
import TaskHeaderAdmin from "../../components/TaskHeaderAdmin/TaskHeaderAdmin";
import TaskCard from "../../components/TaskCard/TaskCard";
import TimeBar from "../../components/TimeBar/TimeBar";
import FilterBar from "../../components/FilterBar/FilterBar";
import foldersImg from "../../assets/images/folders.png";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import "overlayscrollbars/overlayscrollbars.css";
import styles from "./AdminView.module.css";

import FormCheckIcon from "../../assets/svgs/formcheck.svg";
import CloseIcon from "../../assets/svgs/close.svg";
import { SpinnerComponent } from "../../components/Spinner";

const AdminView = ({ empleado, refreshSignal }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [allTasksBase, setAllTasksBase] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const fechaParam = searchParams.get("date");

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      return fechaParam;
    }

    return moment().format("YYYY-MM-DD");
  });
  const [notificationMessage, setNotificationMessage] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [orderOption, setOrderOption] = useState("priority");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [activeCounter, setActiveCounter] = useState({ userId: null, type: null });
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [queryTaskId, setQueryTaskId] = useState(null);
  const [initialDepartmentFromQuery, setInitialDepartmentFromQuery] = useState(null);
  const [queryHighlightType, setQueryHighlightType] = useState(null);
  const [loadingTaks, setLoadingTaks] = useState(false);

  const hasAppliedInitialParams = useRef(false);
  const pendingEmployeeFromQueryRef = useRef(null);
  const fetchUsersRequestRef = useRef(0);

  const getFirstDepartment = (user) => {
    if (!user?.Departamentos) return null;

    if (Array.isArray(user.Departamentos)) {
      return user.Departamentos[0] || null;
    }

    if (typeof user.Departamentos === "string") {
      return user.Departamentos || null;
    }

    return null;
  };

  const userListRef = useRef(null);

  const handleToggleCounter = (userId, type, willBeActive) => {
    if (willBeActive) setActiveCounter({ userId, type });
    else setActiveCounter({ userId: null, type: null });
  };

  const handleFilteredUsersChange = useCallback((filtered) => {
    setFilteredUsers(filtered);
  }, []);

  const reorderAllTasks = (list, order) => {
    const sortedList = [...list].sort((a, b) => {
      const warningsA = a.tasks.filter((t) => t.IDOrigen < 0).length;
      const warningsB = b.tasks.filter((t) => t.IDOrigen < 0).length;
      const notesA = a.tasks.filter((t) => t.Notas !== "").length;
      const notesB = b.tasks.filter((t) => t.Notas !== "").length;
      const totalA = a.tasks.reduce((sum, t) => sum + t.Minutos, 0);
      const totalB = b.tasks.reduce((sum, t) => sum + t.Minutos, 0);

      switch (order) {
        case "priority":
          if (warningsA !== warningsB) return warningsB - warningsA;
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);

        case "maxLoad":
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);

        case "minLoad":
          if (totalA !== totalB) return totalA - totalB;
          return a.user.Nombre.localeCompare(b.user.Nombre);

        case "alphabetical":
          return a.user.Nombre.localeCompare(b.user.Nombre);

        case "notesCount":
          if (notesA !== notesB) return notesB - notesA;
          return a.user.Nombre.localeCompare(b.user.Nombre);

        default:
          if (totalA !== totalB) return totalB - totalA;
          return a.user.Nombre.localeCompare(b.user.Nombre);
      }
    });

    const activeUsers = sortedList.filter(
      (item) => Number(item.user?.TurnoReal) !== 3
    );

    const dimmedUsers = sortedList.filter(
      (item) => Number(item.user?.TurnoReal) === 3
    );

    return [...activeUsers, ...dimmedUsers];
  };

  const allTasks = useMemo(() => {
    return reorderAllTasks(allTasksBase, orderOption);
  }, [allTasksBase, orderOption]);

  const fetchUsers = async () => {
    const requestId = ++fetchUsersRequestRef.current;

    try {
      setIsLoadingUsers(true);
      const data = await getUsers({ fecha: selectedDate });

      if (requestId !== fetchUsersRequestRef.current) return;

      if (data?.Usuarios && Array.isArray(data.Usuarios)) {
        setUsers(data.Usuarios);

        setFilteredUsers((prevFiltered) => {
          if (!prevFiltered || prevFiltered.length === 0) {
            return data.Usuarios;
          }

          const newIds = new Set(data.Usuarios.map((u) => String(u.id)));

          return prevFiltered.filter((u) => newIds.has(String(u.id)));
        });

        if (data.Usuarios.length === 0) {
          setAllTasksBase([]);
          setActiveCounter({ userId: null, type: null });
        }
      } else {
        console.error("Respuesta inesperada de getUsers.php:", data);
        setUsers([]);
        setFilteredUsers([]);
        setSelectedUserId(null);
        setAllTasksBase([]);
        setActiveCounter({ userId: null, type: null });
      }

    } catch (error) {
      if (requestId !== fetchUsersRequestRef.current) return;

      console.error("Error al cargar usuarios:", error);
      setUsers([]);
      setFilteredUsers([]);
      setSelectedUserId(null);
      setAllTasksBase([]);
      setActiveCounter({ userId: null, type: null });

    } finally {
        setIsLoadingUsers(false);
    }
  };

  const fetchAllUsersTasks = async (date) => {
    if (!users.length) {
      setAllTasksBase([]);
      return;
    }

    try {
      setLoadingTaks(true);
      const data = await getDailyByDate({ fecha: date, SoloDia: date });

      if (data?.Daily || data?.future_tasks) {
        const tareaDefs = data.Tareas || [];
        const proyectoDefs = data.Proyectos || [];

        const dailyList = Array.isArray(data?.Daily) ? data.Daily : [];
        const futureList = Array.isArray(data?.future_tasks) ? data.future_tasks : [];

        const mergedTasks = [...dailyList, ...futureList];

        const tasksOfSelectedDay = mergedTasks.filter(
          (task) =>
            moment(task.Fecha).format("YYYY-MM-DD") ===
            moment(date).format("YYYY-MM-DD")
        );

        const allTasksArray = tasksOfSelectedDay
          .filter((task) => task.Automatica !== 1)
          .map((task) => {
            const startDate = task.Inicio
              ? moment(`${task.Fecha} ${task.Inicio}`, "YYYY-MM-DD HH:mm:ss").toDate()
              : moment(task.Fecha, "YYYY-MM-DD").startOf("day").toDate();

            const endDate = task.Inicio
              ? moment(startDate).add(task.Minutos || 0, "minutes").toDate()
              : startDate;

            let tareaName = task.Tarea || "";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea) tareaName = foundTarea.tarea;
            }

            // ✅ Subtarea robusta (incluye caso "Otra")
            let subtareaName = "";
            const isOtra =
              task.Tarea === "Otra" ||
              String(task.IDTarea ?? "") === "-1" ||
              tareaName === "Otra";

            if (isOtra) {
              subtareaName = String(
                task.Otra ??
                task.Subtarea ??
                task.SubtareaTexto ??
                task.subtarea ??
                ""
              ).trim();
            } else if (task.IDTarea && task.IDSubtarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea && Array.isArray(foundTarea.subtareas)) {
                const foundSub = foundTarea.subtareas.find(
                  (s) => s.id.toString() === task.IDSubtarea.toString()
                );
                if (foundSub) subtareaName = foundSub.subtarea;
              }
            } else {
              subtareaName = task.Subtarea || "";
            }

            let proyectoName = task.Proyecto || "";
            if (task.IProyecto) {
              const foundProyecto = proyectoDefs.find(
                (p) => p.id.toString() === task.IProyecto.toString()
              );
              if (foundProyecto) proyectoName = foundProyecto.Proyecto;
            }

            let taskColor = task.Color || "#ccc";
            if (task.IDTarea) {
              const foundTarea = tareaDefs.find(
                (t) => t.id.toString() === task.IDTarea.toString()
              );
              if (foundTarea?.color) {
                taskColor = foundTarea.color.startsWith("#")
                  ? foundTarea.color
                  : `#${foundTarea.color}`;
              }
            }

            return {
              id: task.id,
              title: tareaName,
              start: startDate,
              end: endDate,
              color: taskColor,
              date: moment(task.Fecha, "YYYY-MM-DD").format("YYYY-MM-DD"),
              Minutos: task.Minutos || 0,
              Tarea: tareaName,

              Subtarea: subtareaName,
              subtarea: subtareaName,
              subtask: subtareaName,

              Proyecto: proyectoName,
              Inicio: task.Inicio || null,
              Hecho: task.Hecho || 0,
              IDOrigen: task.IDOrigen ?? 0,
              Origen: task.Origen || null,
              Empleado: task.Empleado,
              Notas: task.Notas,
              Descripcion: task.Descripcion,

              is_projection: !!task.is_projection,
              parent_periodic_id: task.parent_periodic_id ?? null,
            };
          });

        const grouped = users.map((u) => {
          const tasks = allTasksArray.filter(
            (t) => t.Empleado.toString() === u.id.toString()
          );
          return { user: u, tasks };
        });

        setAllTasksBase(grouped);
      } else {
        setAllTasksBase([]);
      }

    } catch (error) {
      console.error("Error al cargar tareas para todos los usuarios:", error);
      setAllTasksBase([]);
    } finally {
      setLoadingTaks(false);
    }
  };

  useEffect(() => {
    const vp = userListRef.current?.osInstance()?.elements().viewport;
    if (vp) vp.scrollTop = 0;
  }, [orderOption]);

  useEffect(() => {
    fetchUsers(selectedDate);
  }, [refreshSignal]);

  useEffect(() => {
    fetchAllUsersTasks(selectedDate);
  }, [users, selectedDate, refreshSignal]);

  useEffect(() => {
    if (users.length > 0 && selectedUserId !== null) {
      if (!users.some((u) => u.id === selectedUserId)) {
        setSelectedUserId(users[0]?.id ?? null);
      }
    }
    if (users.length === 0) {
      setSelectedUserId(null);
      setAllTasksBase([]);
    }
  }, [users]);

  useEffect(() => {
    const fechaParam = searchParams.get("date");
    const empleadoParam = searchParams.get("empleado");
    const taskIdParam = searchParams.get("task_id");
    const notesParam = searchParams.get("notes");

    const shouldOpenNotes = notesParam === "true";

    let usedParams = false;

    if (fechaParam && moment(fechaParam, "YYYY-MM-DD", true).isValid()) {
      setSelectedDate(fechaParam);
      usedParams = true;
    }

    if (empleadoParam) {
      pendingEmployeeFromQueryRef.current = String(empleadoParam);
      usedParams = true;
    }

    if (shouldOpenNotes) {
      setQueryHighlightType("notes");
      setQueryTaskId(null);
      usedParams = true;
    } else if (taskIdParam) {
      setQueryTaskId(String(taskIdParam));
      usedParams = true;
    }

    if (usedParams && !hasAppliedInitialParams.current) {
      hasAppliedInitialParams.current = true;
      navigate(".", { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    if (!users.length || !pendingEmployeeFromQueryRef.current) return;

    const employeeIdFromQuery = pendingEmployeeFromQueryRef.current;

    const foundUser = users.find(
      (u) => String(u.id) === String(employeeIdFromQuery)
    );

    if (!foundUser) {
      pendingEmployeeFromQueryRef.current = null;
      return;
    }

    const userDepartment = getFirstDepartment(foundUser);
    if (userDepartment) {
      setInitialDepartmentFromQuery(userDepartment);
    }

    setSelectedUserId(foundUser.id);
    pendingEmployeeFromQueryRef.current = null;
  }, [users]);

  useEffect(() => {
    if (!selectedUserId || !queryHighlightType) return;

    setActiveCounter({
      userId: selectedUserId,
      type: queryHighlightType,
    });

    setQueryHighlightType(null);
  }, [selectedUserId, queryHighlightType]);

  useEffect(() => {
    if (!allTasks.length) return;

    const visibles = allTasks.filter((item) =>
      filteredUsers.some((u) => u.id === item.user.id)
    );

    if (!visibles.length) {
      if (selectedUserId !== null) setSelectedUserId(null);
      return;
    }

    const isCurrentVisible = visibles.some((v) => v.user.id === selectedUserId);
    if (selectedUserId == null || !isCurrentVisible) {
      setSelectedUserId(visibles[0].user.id);
    }
  }, [allTasks, filteredUsers, selectedUserId]);

  const handleTaskSuccess = (message) => {
    setNotificationMessage(message);
    setTimeout(() => setNotificationMessage(""), 3000);
  };

  const handleCloseBanner = () => setNotificationMessage("");

  const handleSelectUser = (user) => setSelectedUserId(user.id);

  const selectedUserTasks =
    selectedUserId && allTasks.find((item) => item.user.id === selectedUserId)
      ? allTasks.find((item) => item.user.id === selectedUserId).tasks
      : [];

  const sortedAdminTasks = [...selectedUserTasks].sort((a, b) => {
    if (a.Hecho !== b.Hecho) return a.Hecho - b.Hecho;
    const aIsWarning = a.IDOrigen && Math.abs(a.IDOrigen) === a.Empleado;
    const bIsWarning = b.IDOrigen && Math.abs(b.IDOrigen) === b.Empleado;
    if (aIsWarning !== bIsWarning) return aIsWarning ? -1 : 1;
    if (a.sortStart && b.sortStart) return a.sortStart - b.sortStart;
    return a.Tarea.localeCompare(b.Tarea);
  });

  return (
    <div className={styles.adminViewContainer}>
      <div className={styles.leftPanel}>
        <UserCardHeader
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          orderOption={orderOption}
          onOrderChange={setOrderOption}
          empleado={empleado}
        />

        <FilterBar
          orderOption={orderOption}
          onOrderChange={setOrderOption}
          users={users}
          onFilteredUsersChange={handleFilteredUsersChange}
          initialDepartment={initialDepartmentFromQuery}
        />

        <OverlayScrollbarsComponent
          ref={userListRef}
          options={{ scrollbars: { autoHide: "scroll", autoHideDelay: 300 } }}
          style={{ minHeight: "75vh", maxHeight: "75vh", width: "100%" }}
        >
          {isLoadingUsers || loadingTaks ? (
            <SpinnerComponent />
          ) : (
            <div className={styles.userList}>
              {allTasks
                .filter((item) => filteredUsers.some((u) => u.id === item.user.id))
                .map((item) => (
                  <UserCard
                    key={item.user.id}
                    user={item.user}
                    tasks={item.tasks}
                    selectedDate={selectedDate}
                    onSelectUser={handleSelectUser}
                    isSelected={item.user.id === selectedUserId}
                    admin={true}
                    activeCounter={
                      activeCounter.userId === item.user.id ? activeCounter.type : null
                    }
                    onToggleCounter={(type, willBeActive) =>
                      handleToggleCounter(item.user.id, type, willBeActive)
                    }
                  />
                ))}
            </div>
          )}
        </OverlayScrollbarsComponent>
      </div>

      <div className={styles.rightPanel}>
        <TaskHeaderAdmin
          userName={selectedUserId ? users.find((u) => u.id === selectedUserId)?.Nombre : ""}
          selectedDate={selectedDate}
        />

        <div className={styles.adminContent} style={{ backgroundColor: "#F8F8F8" }}>
          {selectedUserId &&
            (notificationMessage ? (
              <div className={styles.notificationBanner}>
                <div className={styles.iconContainer}>
                  <img src={FormCheckIcon} alt="Check icon" className={styles.iconCheck} />
                </div>
                {notificationMessage}
                <button className={styles.closeButton} onClick={handleCloseBanner}>
                  <img src={CloseIcon} alt="Close icon" className={styles.closeIcon} />
                </button>
              </div>
            ) : (
              <TimeBar tasks={selectedUserTasks} selectedDate={selectedDate} admin={false} />
            ))}

          {selectedUserId ? (
            <OverlayScrollbarsComponent
              options={{ scrollbars: { autoHide: "scroll", autoHideDelay: 300 } }}
              style={{ maxHeight: "75vh", width: "100%" }}
            >
              <div className={styles.adminTaskList}>
                {selectedUserTasks.length > 0 ? (
                  sortedAdminTasks.map((task) => (
                    <div key={task.id} className={styles.adminTaskItem}>
                      <TaskCard
                        task={task}
                        user={users.find((u) => u.id === selectedUserId)}
                        key={task.id}
                        updateTaskInState={() => { }}
                        onSuccess={handleTaskSuccess}
                        admin={true}
                        allUsers={users}
                        allTasks={allTasks}
                        selectedDate={selectedDate}
                        empleado={empleado}
                        activeCounterType={
                          activeCounter.userId === selectedUserId ? activeCounter.type : null
                        }
                        autoOpenReassignTaskId={queryTaskId}
                        onAutoOpenReassignConsumed={(openedTaskId) => {
                          setQueryTaskId((prev) =>
                            String(openedTaskId) === String(prev) ? null : prev
                          );
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <div className={styles.noTaskContainer}>
                    <img src={foldersImg} alt="No tasks" className={styles.noTaskImage} />
                    <p className={styles.noTaskText}>
                      Este usuario no tiene tareas asignadas para este día.
                    </p>
                  </div>
                )}
              </div>
            </OverlayScrollbarsComponent>
          ) : (
            <div />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminView;
